import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
} from "@mui/material";
import {
  DRIP_CAMPAIGN_LIST_ENDPOINT,
  DRIP_CAMPAIGN_PAUSE_ENDPOINT,
  DRIP_CAMPAIGN_RESUME_ENDPOINT,
} from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import {
  Add,
  Description,
  Pause,
  PlayArrow,
  Visibility,
  MoreVert,
} from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { formatWithTzLabel } from "../../utils/dateUtils";

const DripMarketing = ({ onNavigate }) => {
  const [dripCampaigns, setDripCampaigns] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const { getValidToken } = useAuth();

  // --- Menu state ---
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event, campaign) => {
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCampaign(null);
  };

  const statusStyles = {
    paused: {
      bg: "#F96800", // orange
      color: "#FFFFFF",
    },
    active: {
      bg: "#2E7D32", // green
      color: "#FFFFFF",
    },
    completed: {
      bg: ColorsConstants.primaryBlue100, // blue
      color: "#FFFFFF",
    },
  };

  const fetchData = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(DRIP_CAMPAIGN_LIST_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      setDripCampaigns(await response.json());
    } catch (err) {
      console.log("Error fetching drip campaigns: ", err);
    }
  };

  const pauseCampaign = async (id) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(`${DRIP_CAMPAIGN_PAUSE_ENDPOINT}${id}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        method: "POST",
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      fetchData();
    } catch (err) {
      console.log("Error pausing drip campaign: ", err);
    }
  };

  const resumeCampaign = async (id) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(`${DRIP_CAMPAIGN_RESUME_ENDPOINT}${id}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        method: "POST",
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      fetchData();
    } catch (err) {
      console.log("Error resuming drip campaign: ", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columns = [
    {
      id: "name",
      label: "Name",
      minWidth: 200,
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            cursor: "pointer",
            width: "fit-content",
            color: "#353535",
          }}
          onClick={() =>
            onNavigate("drip_marketing_campaign_details", {
              dripCampaignId: row?.id,
            })
          }
        >
          {row.name}
        </Typography>
      ),
    },
    {
      id: "trigger_type",
      label: "Trigger Type",
      minWidth: 160,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 400, textTransform: "capitalize" }}>
          {row?.trigger_type?.replaceAll("_", " ")}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 120,
      render: (row) => (
        <Chip
          label={row.status}
          size="small"
          sx={{
            backgroundColor: statusStyles[row.status]?.bg ?? "#E0E0E0",
            color: statusStyles[row.status]?.color ?? "#424242",
            fontWeight: 500,
            textTransform: "capitalize",
          }}
        />
      ),
    },
    {
      id: "start_date",
      label: "Start Date",
      minWidth: 160,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
          {row?.start_date}
        </Typography>
      ),
    },
    {
      id: "end_date",
      label: "End Date",
      minWidth: 160,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
          {row?.end_date}
        </Typography>
      ),
    },
    {
      id: "created_by",
      label: "Created By",
      minWidth: 180,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
          {row?.created_by}
        </Typography>
      ),
    },
    {
      id: "created_at",
      label: "Created At",
      minWidth: 180,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
          {formatWithTzLabel(row?.created_at_iso)}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "center",
      render: (row) => (
        <IconButton onClick={(e) => handleMenuOpen(e, row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: "#fafafa" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          mb: 2,
        }}
      >
        <ButtonOne
          startIcon={<Add />}
          variant="contained"
          onClick={() => onNavigate("create_drip_marketing_campaign")}
          title={"Create Campaign"}
        ></ButtonOne>
      </Box>

      <AppTable
        columns={columns}
        rows={dripCampaigns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
        emptyMessage="No campaigns found"
        count={dripCampaigns.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        paperSx={{ border: "1px solid #DCDFE5", borderRadius: "12px", mb: 2 }}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
        }}
        rowSx={(row, index) => ({
          backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
          cursor: "default",
          "&:hover": { backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC" },
          "& td": { borderBottom: "none", borderTop: "none" },
        })}
      />

      {/* Floating Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 180 },
        }}
      >
        <MenuItem
          onClick={() => {
            onNavigate("drip_marketing_campaign_details", {
              dripCampaignId: selectedCampaign?.id,
            });
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="View Details" />
        </MenuItem>

        <MenuItem
          onClick={() => {
            // Add report view action here
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Description fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="View Report" />
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (selectedCampaign?.status === "paused") {
              resumeCampaign(selectedCampaign.id);
            } else {
              pauseCampaign(selectedCampaign.id);
            }
            handleMenuClose();
          }}
          disabled={!["active", "paused"].includes(selectedCampaign?.status)}
        >
          <ListItemIcon>
            {selectedCampaign?.status === "paused" ? (
              <PlayArrow color="success" fontSize="small" />
            ) : (
              <Pause color="warning" fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              selectedCampaign?.status === "paused"
                ? "Resume Campaign"
                : "Pause Campaign"
            }
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DripMarketing;
